import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { FadeUp } from "./Reveal";
import { PROJECT_TYPES } from "@/data";
import { trackLead } from "@/lib/pixels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: "",
  email: "",
  company: "",
  phone: "",
  project_type: "",
  location: "",
  message: "",
};

function Field({ label, name, type = "text", value, onChange, required, testid, placeholder }) {
  return (
    <label className="block">
      <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
        {label} {required && <span className="text-[#FF4D00]">*</span>}
      </span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-3 w-full bg-transparent border-b-2 border-[#333] py-3 font-mono text-white text-base placeholder-[#555] focus:border-[#FF4D00] focus:outline-none transition-colors"
      />
    </label>
  );
}

export default function QuoteForm() {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_type) {
      toast.error("Select a project type.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      trackLead({ content_name: form.project_type });
      toast.success("Request received. Our team will be in touch within one working day.");
      setForm(emptyForm);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-40 bg-[#0A0A0A] border-t border-[#333]" data-testid="contact">
      <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <FadeUp className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#FF4D00]" aria-hidden="true" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF4D00]">Get a Quote</span>
          </div>
          <h2 className="font-display font-bold uppercase tracking-tighter text-white text-4xl sm:text-5xl lg:text-7xl leading-[0.9] mb-10">
            Scope The<br />Job
          </h2>
          <p className="font-mono font-light text-[#A3A3A3] text-base leading-relaxed max-w-md mb-10">
            Tell us what you're building. We'll return a costed, drawn-up temporary
            works proposal — no guesswork, no vague day rates.
          </p>
          <div className="space-y-4 font-mono text-sm text-[#A3A3A3]">
            <div className="flex justify-between border-b border-[#333] pb-3">
              <span className="uppercase tracking-[0.15em] text-[#555]">Email</span>
              <span className="text-white">build@cretexa.ca</span>
            </div>
            <div className="flex justify-between border-b border-[#333] pb-3">
              <span className="uppercase tracking-[0.15em] text-[#555]">Phone</span>
              <span className="text-white">+1 (403) 555-0142</span>
            </div>
            <div className="flex justify-between border-b border-[#333] pb-3">
              <span className="uppercase tracking-[0.15em] text-[#555]">Yard</span>
              <span className="text-white">Calgary, AB</span>
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.1} className="lg:col-span-7">
          <form onSubmit={onSubmit} className="space-y-8" data-testid="quote-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <Field label="Name" name="name" value={form.name} onChange={onChange} required testid="input-name" placeholder="Jane Doe" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="input-email" placeholder="you@company.com" />
              <Field label="Company" name="company" value={form.company} onChange={onChange} testid="input-company" placeholder="Optional" />
              <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testid="input-phone" placeholder="Optional" />
            </div>

            <label className="block">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
                Project Type <span className="text-[#FF4D00]">*</span>
              </span>
              <select
                name="project_type"
                value={form.project_type}
                onChange={onChange}
                required
                data-testid="select-project-type"
                className="mt-3 w-full bg-transparent border-b-2 border-[#333] py-3 font-mono text-white text-base focus:border-[#FF4D00] focus:outline-none transition-colors appearance-none cursor-pointer"
              >
                <option value="" disabled className="bg-[#1A1A1A]">Select a discipline…</option>
                {PROJECT_TYPES.map((t) => (
                  <option key={t} value={t} className="bg-[#1A1A1A]">{t}</option>
                ))}
              </select>
            </label>

            <Field label="Site Location" name="location" value={form.location} onChange={onChange} testid="input-location" placeholder="City / postcode" />

            <label className="block">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
                Project Details <span className="text-[#FF4D00]">*</span>
              </span>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                required
                rows={4}
                data-testid="input-message"
                placeholder="Tell us about scope, elevations, timelines…"
                className="mt-3 w-full bg-transparent border-b-2 border-[#333] py-3 font-mono text-white text-base placeholder-[#555] focus:border-[#FF4D00] focus:outline-none transition-colors resize-none"
              />
            </label>

            <button
              type="submit"
              disabled={loading}
              data-testid="submit-quote"
              className="group inline-flex items-center gap-3 bg-[#FF4D00] text-black font-mono uppercase tracking-[0.15em] text-sm px-10 py-5 hover:bg-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />}
              {loading ? "Sending…" : "Submit Request"}
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
}
